<template>
	<div class="list">
		<div v-for="user in data" :key="user.id" class="list__item pb--0">
			<h4 class="list__title mt--0 mb--0">{{ user.name }}</h4>
			<div
				v-for="simulation in user.simulations"
				:key="simulation.studentId"
				class="list__content pl--0"
				:class="{ 'content--expanded': simulation.isExpanded }"
			>
				<div class="content__title">{{ simulation.name }}</div>
				<button
					class="content__button btn btn-link btn--bold"
					@click="toggleSimulation(simulation)"
				>
					{{ simulation.isExpanded ? $t('hideDetails') : $t('showDetails') }}
					<svgicon
						class="icon--clickable icon--small"
						name="arrow-right"
					></svgicon>
				</button>
				<div class="list__content-expandable mt--2">
					<ja-loader
						v-if="simulation.isExpanded && !simulation.isDataFetched"
					/>
					<div v-else class="expandable-item">
						<div class="checklist mb--2">
							<div
								v-for="(checkItem, index) in simulation.progress"
								:key="index"
								class="checklist__item"
							>
								<svgicon :name="checkItem.icon" :class="checkItem.iconClass" />
								<span class="checklist__text">{{ checkItem.name }}</span>
							</div>
							<div class="checklist__item">
								<span class="checklist__text">
									{{ $tc('attemptCounter', simulation.simulationTry) }}
									<button
										v-if="simulation.simulationTry > 1"
										class="btn btn-link btn--bold"
										@click="openSimAttemptsModal(simulation)"
									>
										( {{ $t('showAll') }})
									</button>
								</span>
							</div>
						</div>
						<div class="table-view">
							<div
								v-for="(chunk, index) in simulation.reportData"
								:key="index"
								class="table__row"
							>
								<div
									v-for="(record, dataIndex) in chunk"
									:key="dataIndex"
									class="table__item"
								>
									<span class="table__title mb--1">
										{{ record.name }}
									</span>
									<span class="table__content">
										{{ parsePriceValues(record) }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import StudentsService from '@/services/StudentsService';
import Loader from '@ui/Loader';
import _ from 'lodash';
import { formatPricePears } from '@/helpers/utils';

export default {
	components: {
		jaLoader: Loader
	},
	filters: {
		currency(value) {
			return value ? `${this.$t('currencySymbol')}${value}` : '-';
		}
	},
	props: {
		items: {
			type: Array,
			default: () => []
		},
		lifeStage: {
			type: String,
			default: ''
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			data: [],
			checklistStatuses: {
				INCOMPLETED: 'checkmarks/not-completed',
				COMPLETED: 'checkmarks/completed',
				IN_PROGRESS: 'checkmarks/in-progress'
			}
		};
	},
	watch: {
		items() {
			this.dataAdapter();
		}
	},
	mounted() {
		this.dataAdapter();
	},
	methods: {
		dataAdapter() {
			this.data = this.items.map(item => {
				return {
					id: item.id,
					name: `${item.firstName} ${item.lastName}`,
					simulations: item.simulations.map(sim => this.adaptSimulation(sim))
				};
			});
		},
		adaptSimulation(sim) {
			return {
				id: sim.simulation.id,
				name: sim.simulation.sessionName,
				simulationType: sim.simulation.simulationType,
				studentId: sim.id,
				userId: sim.userId,
				simulationTry: sim.simulationTry,
				reportData: sim.studentAtAGlanceReport
					? _.chunk(sim.studentAtAGlanceReport[0].data, 4)
					: [],
				isExpanded: false,
				isDataFetched: false,
				progress: this.adaptSimulationForProgress(sim)
			};
		},
		adaptSimulationForProgress(sim) {
			if (!sim.hasOwnProperty('isAvatarSelected')) {
				return [];
			}
			// Those items are available for every type of simulation
			let defaultProgress = [
				{
					name: this.$t('avatarSelectionProgressLbl'),
					...this.getChecklistIconData(sim.isAvatarSelected)
				},
				{
					name: this.$t('preQuestionsProgressLbl'),
					...this.getChecklistIconData(sim.answeredQuestions.length)
				},
				{
					name: this.$t('interestsProgressLbl'),
					...this.getChecklistIconData(sim.interests.length)
				}
			];

			// destruct only the variable because it is much easier to read the code
			const { simulationType } = sim.simulation;
			// What should be the result for Icons
			if (simulationType && simulationType.toLowerCase().includes('advanced')) {
				defaultProgress.push(
					{
						name: this.$t('goalsProgressLbl'),
						...this.getChecklistIconData(!!sim.career)
					},
					{
						name: this.$t('careerProgressLbl'),
						...this.getChecklistIconData(!!sim.education)
					},
					{
						name: this.$t('educationProgressLbl'),
						...this.getChecklistIconData(sim.goals.length)
					}
				);
			}

			// build categories progress
			let categories = {};
			// on initial load we don't have report because we don't need it (perfomance)
			if (sim.studentAtAGlanceReport) {
				const { completed, available } = sim.studentAtAGlanceReport[0];

				categories = {
					...this.getChecklistIconData(completed, available),
					name: this.$t('completedCategoriesLabel', [completed, available])
				};
			}

			defaultProgress.push(categories);

			return defaultProgress;
		},
		getChecklistIconData(status, checkAgainst) {
			const baseData = {
				icon: this.checklistStatuses.INCOMPLETED,
				iconClass: 'icon--danger'
			};

			if (status) {
				baseData.icon = this.checklistStatuses.COMPLETED;
				baseData.iconClass = 'icon--primary-light';
			}

			if (checkAgainst && status >= 1 && status < checkAgainst) {
				baseData.icon = this.checklistStatuses.IN_PROGRESS;
				baseData.iconClass = 'icon--success';
			}

			return baseData;
		},
		toggleSimulation(simulation) {
			simulation.isExpanded = !simulation.isExpanded;

			if (simulation.isDataFetched) {
				return;
			}

			StudentsService.getSimulationDataByStudent(
				simulation,
				this.lifeStage
			).then(({ data }) => {
				const simData = this.adaptSimulation(data.simulation[0]);
				// This is needed to avoid deep watch and to keep reactivity
				Object.keys(simData).forEach(key => {
					if (key !== 'isExpanded') {
						simulation[key] = simData[key];
					}
				});

				simulation.isDataFetched = true;
			});
		},
		parsePriceValues({ actual, budgeted }) {
			return formatPricePears(actual, budgeted);
		},
		openSimAttemptsModal(sim) {
			this.$emit('showSimulationAttempts', sim);
		}
	}
};
</script>

<style lang="scss"></style>
